import { ReactNode, createContext, isValidElement, useContext } from 'react';

declare global {
  interface Window {
    dataLayer: any;
  }
}

export const getGoogleAnalyticsId = () => {
  if (document?.cookie?.length) {
    const gaCookie = document.cookie.match(/_ga=(.+?);/);

    if (gaCookie?.length) {
      const gaClientID = gaCookie[1].split('.').slice(-2).join('.');

      return gaClientID ? gaClientID : '';
    }
  }

  return '';
};

export type TrackingProps = {
  segment?: { event?: string; properties?: any };
  gtm: { event: string; properties: Record<string, any> };
};

export const track = ({ segment, gtm }: TrackingProps) => {
  window.dataLayer = window.dataLayer || [];
  const isPayFitAppUser = document?.cookie
    ?.split('; ')
    .find(row => row.startsWith('payfit-app-user='))
    ?.split('=')[1] === 'true';

  if (typeof window !== 'undefined') {
    if (segment) {
      const { analytics } = window as any;
      if (analytics && segment?.event) {
        analytics.track(segment.event, { ...segment.properties, isPayFitAppUser });
      }
    }
    if (gtm) {
      const eventObject = {
        event: gtm.event,
        properties: { ...gtm.properties, isPayFitAppUser },
        gaClientID: getGoogleAnalyticsId(),
      };

      try {
        window.dataLayer.push(eventObject);
      } catch (oO) {
        console.error(oO);
      }
    }
  }
};

export const hubspotIdentify = (email: string) => {
  if (typeof window !== 'undefined') {
    const _hsq = window?._hsq ? window._hsq : [];

    _hsq.push([
      'identify',
      {
        email,
      },
    ]);
  }
};

export const page = (name: string, properties: Record<string, any>) => {
  if (typeof window !== 'undefined') {
    const eventObject = {
      event: 'Pageview',
      name,
      properties,
      gaClientID: getGoogleAnalyticsId(),
    };

    window.dataLayer = window.dataLayer || [];
    try {
      window.dataLayer.push(eventObject);
    } catch (oO) {
      console.error(oO);
    }
  }
};

export const TrackingZone = createContext('middle');
export const Tracking = createContext({
  page: 'not-available',
  type: 'not-available',
});

export const useTracking = () => {
  const trackingZone = useContext<string>(TrackingZone);
  const trackingPage = useContext<any>(Tracking);

  return { trackingZone, trackingPage };
};

export const findStringInReactNode = (
  children: ReactNode,
): string | undefined => {
  if (typeof children === 'string') return children;
  if (Array.isArray(children))
    return findStringInReactNode(children[0].props?.children);
  if (isValidElement(children))
    return findStringInReactNode(children.props?.children);
  return undefined;
};

export const findTitle = (props, children) => {
  const title =
    (typeof props?.title === 'string' && props?.title) ||
    (typeof props?.title?.props?.title === 'string' &&
      props?.title?.props?.title) ||
    (typeof children?.props?.title === 'string' && children?.props?.title) ||
    findStringInReactNode(children);
  return title;
};

export const getMenuTrackEvent = ({
  locale,
  menuItem,
}: {
  locale: string;
  menuItem: any;
}) => {
  const path =
    menuItem?.internalUrl?.slug || menuItem?.internalLink?.slug || 'external';
  return `WEB-${locale} | Menu | ${path}`;
};
